import React from 'react';
import { useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { Table } from 'react-bootstrap';

import { selectStudents } from '../../../redux/reducers/StudentsReducer';
import { selectStudentsResults } from '../../../redux/reducers/StudentsResultsReducer';

import { RiskKeys } from '../../../types/RiskKeys';
import { ProtectionKeys } from '../../../types/ProtectionKeys';
import NormsTable from '../NormsTable/NormsTable';

const StudentResultsRenderer = () => {
  const { userId: userIdString } = useParams<{ userId: string | undefined }>();
  const userId = parseInt(userIdString ?? '-1', 10);

  const user = useSelector(selectStudents)?.[userId];
  const userName = user?.username;

  const studentResults = useSelector(selectStudentsResults)?.[userId];

  if (!userName) return <h3>Загрузка...</h3>;
  if (!studentResults) return <h3>Данный ученик не прошел тестирование</h3>;

  const riskKeys = [
    { label: 'ППЗ', key: 'PPZ' },
    { label: 'ПВГ', key: 'PVG' },
    { label: 'ПАУ', key: 'PAU' },
    { label: 'СР', key: 'SR' },
    { label: 'ИМ', key: 'IM' },
    { label: 'ТР', key: 'TR' },
    { label: 'ФР', key: 'FR' },
    { label: 'ДЕ', key: 'DE' },
  ] as Array<{ label: string; key: keyof RiskKeys }>;

  const protectionKeys = [
    { label: 'ПР', key: 'PR' },
    { label: 'ПО', key: 'PO' },
    { label: 'СА', key: 'SA' },
    { label: 'СП', key: 'SP' },
    { label: 'СЭ', key: 'SE' },
    { label: 'АН', key: 'AN' },
    { label: 'ФУ', key: 'FU' },
    { label: 'ДО', key: 'DO' },
  ] as Array<{ label: string; key: keyof ProtectionKeys }>;

  const cellColor = (score: number) => {
    if (score === 0) return 'transparent';
    if (score >= 4 && score <= 7) return 'limegreen';
    return 'tomato';
  };

  const protectionFactorsTable = (
    <Table
      style={{ fontSize: '0.9rem', maxWidth: '720px' }}
      striped
      bordered
      responsive
    >
      <thead>
        <tr>
          <th>&nbsp;</th>

          {protectionKeys.map(({ label }) => (
            <th key={label}>{label}</th>
          ))}
        </tr>
      </thead>

      <tbody>
        <tr>
          <td>Баллы</td>

          {protectionKeys.map(({ key }) => (
            <td key={key}>{studentResults[key]}</td>
          ))}
        </tr>

        <tr>
          <td>Стэны</td>

          {protectionKeys.map(({ key }) => (
            <td
              key={key}
              style={{
                background: cellColor(studentResults[key]),
              }}
            >
              {studentResults[`stans${key}` as keyof ProtectionKeys]}
            </td>
          ))}
        </tr>
      </tbody>
    </Table>
  );

  const riskFactorsTable = (
    <Table
      style={{ fontSize: '0.9rem', maxWidth: '720px' }}
      striped
      bordered
      responsive
    >
      <thead>
        <tr>
          <th>&nbsp;</th>

          {riskKeys.map(({ label }) => (
            <th key={label}>{label}</th>
          ))}
        </tr>
      </thead>

      <tbody>
        <tr>
          <td>Баллы</td>

          {riskKeys.map(({ key }) => (
            <td key={key}>{studentResults[key]}</td>
          ))}
        </tr>

        <tr>
          <td>Стэны</td>

          {riskKeys.map(({ key }) => (
            <td
              key={key}
              style={{
                background: cellColor(studentResults[key]),
              }}
            >
              {studentResults[`stans${key}` as keyof ProtectionKeys]}
            </td>
          ))}
        </tr>
      </tbody>
    </Table>
  );

  return (
    <section className="d-flex flex-column justify-content-center">
      <div>
        <h3 className="mb-5">{userName}</h3>

        <h4 className="mb-4 mt-4">Нормы</h4>
        <NormsTable />

        <h6 className="mb-4 mt-4">КВЕРИПО: {studentResults?.KVERIPO ?? 0}</h6>
        <h6 className="mb-5 mt-4">ИРП: {studentResults?.IRP ?? 0}%</h6>

        <h4 className="mb-4 mt-4">Факторы защиты</h4>
        {protectionFactorsTable}

        <h4 className="mb-4 mt-4">Факторы риска</h4>
        {riskFactorsTable}
      </div>
    </section>
  );
};

export default StudentResultsRenderer;
